"use client";

// Premium OS-Grade Floating Windows - Spatial Tiles
// Adapted from Karma AC v11 - Draggable, Stackable, Magnetic, Minimizable, Fullscreen

import React, { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { haptics } from "@/lib/utils/haptics";
import { eventBus } from "@/lib/utils/eventBus";
import { GripHorizontal } from "lucide-react";
import type { AiProfile } from "@/types/ai";

export interface SpatialTileData {
  id: string;
  name: string;
  icon?: string;
  type: "chat" | "project" | "feature" | "marketplace";
  x: number;
  y: number;
  width: number;
  height: number;
  zIndex: number;
  minimized?: boolean;
  maximized?: boolean;
  content?: React.ReactNode;
  metadata?: any;
  /** When true, the tile renders without its own title bar/padding so the content fills the whole frame. */
  chromeless?: boolean;
  /** Every tile currently shares the canvas's one active profile — carried per-tile so the glow ring is already forward-compatible with per-tile branding. */
  profile?: AiProfile | null;
  /** A response arrived while this tile wasn't the active one — drives the urgency glow pulse until it's focused. */
  hasUnseen?: boolean;
}

interface Props {
  tile: SpatialTileData;
  // Minimize/maximize no longer have call sites here — those controls moved
  // to the consciousness bar for whichever tile is active (see
  // SPATIAL:ACTIVE_TILE_ACTION in SpatialCommandCenter.tsx). onClose is
  // still used directly, by the swipe-to-dismiss gesture below.
  onClose: (id: string) => void;
  onResize: (id: string, width: number, height: number) => void;
  onMoveFront: (id: string) => void;
  onDragPosition: (id: string, x: number, y: number) => void;
  onSnap?: (id: string, x: number, y: number, threshold: number) => { x: number; y: number } | null;
  accent?: string;
  /** Rendered inside a scroll-snap carousel item (see SpatialCommandCenter.tsx) — fills its flex parent via relative sizing instead of the usual absolute x/y/width/height positioning, and drag/resize don't apply. */
  carousel?: boolean;
}

export default function SpatialTile({
  tile,
  onClose,
  onResize,
  onMoveFront,
  onDragPosition,
  onSnap,
  accent = "#C1A2FF",
  carousel = false,
}: Props) {
  const chromeless = tile.chromeless || false;
  const start = useRef({ x: 0, y: 0, left: 0, top: 0 });
  const resizeStart = useRef({ x: 0, y: 0, width: 0, height: 0, left: 0, top: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);

  const SNAP_THRESHOLD = 20;
  const SWIPE_CLOSE_THRESHOLD = 120;
  const OFFSCREEN_PEEK = 80;

  // Keep tiles above the mobile keyboard when the visual viewport shrinks
  useEffect(() => {
    if (tile.maximized || typeof window === "undefined" || !window.visualViewport) return;

    const handleViewportChange = () => {
      const vv = window.visualViewport;
      if (!vv || window.innerWidth > 767) return;

      const viewportBottom = vv.height - 16;
      const tileBottom = tile.y + tile.height;
      if (tileBottom <= viewportBottom) return;

      let newY = tile.y;
      let newHeight = Math.max(200, viewportBottom - tile.y - 16);
      if (newHeight < 240) {
        newY = 16;
        newHeight = Math.max(200, viewportBottom - newY - 16);
      }
      if (newY !== tile.y) onDragPosition(tile.id, tile.x, newY);
      if (newHeight !== tile.height) onResize(tile.id, tile.width, newHeight);
    };

    window.visualViewport.addEventListener("resize", handleViewportChange);
    return () => window.visualViewport?.removeEventListener("resize", handleViewportChange);
  }, [tile.maximized, tile.height, tile.width, tile.y, tile.id, onDragPosition, onResize]);

  const onDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    if ((e.target as HTMLElement).closest('.tile-controls') || (e.target as HTMLElement).closest('.resize-handle')) {
      return;
    }
    haptics.hover();
    setIsDragging(true);
    onMoveFront(tile.id);
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    start.current = { x: clientX, y: clientY, left: tile.x, top: tile.y };
    const startTime = Date.now();
    let lastX = tile.x;
    let lastY = tile.y;

    const onMove = (ev: MouseEvent | TouchEvent) => {
      if ('touches' in ev) ev.preventDefault();
      const cx = 'touches' in ev ? ev.touches[0].clientX : ev.clientX;
      const cy = 'touches' in ev ? ev.touches[0].clientY : ev.clientY;
      const dx = cx - start.current.x;
      const dy = cy - start.current.y;
      let nx = start.current.left + dx;
      let ny = start.current.top + dy;

      if (onSnap) {
        const snapResult = onSnap(tile.id, nx, ny, SNAP_THRESHOLD);
        if (snapResult) {
          nx = snapResult.x;
          ny = snapResult.y;
        }
      }

      // Allow a small off-screen peek so users can swipe/throw tiles away
      nx = Math.max(-OFFSCREEN_PEEK, Math.min(nx, window.innerWidth - tile.width + OFFSCREEN_PEEK));
      ny = Math.max(-OFFSCREEN_PEEK, Math.min(ny, window.innerHeight - tile.height - 20));

      lastX = nx;
      lastY = ny;
      onDragPosition(tile.id, nx, ny);
    };

    const onUp = () => {
      setIsDragging(false);
      window.removeEventListener("mousemove", onMove as EventListener);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove as EventListener);
      window.removeEventListener("touchend", onUp);
      window.removeEventListener("touchcancel", onUp);

      const elapsed = Math.max(1, Date.now() - startTime);
      const distance = Math.hypot(lastX - start.current.left, lastY - start.current.top);
      const velocity = distance / elapsed;
      const dx = lastX - start.current.left;
      const dy = lastY - start.current.top;
      const isOffScreen =
        lastX < -OFFSCREEN_PEEK ||
        lastY < -OFFSCREEN_PEEK ||
        lastX > window.innerWidth - tile.width + OFFSCREEN_PEEK ||
        lastY > window.innerHeight - tile.height - 20;
      const fastSwipe = velocity > 1.2 && distance > SWIPE_CLOSE_THRESHOLD;
      const horizontalSwipe =
        velocity > 0.5 &&
        distance > 80 &&
        Math.abs(dx) > Math.abs(dy) * 1.2;

      if (horizontalSwipe) {
        haptics.click();
        eventBus.emit("SPATIAL:TILE_SWIPE", {
          direction: dx > 0 ? "right" : "left",
          tileId: tile.id,
        });
        // Snap the tile back to its original position instead of dragging it.
        onDragPosition(tile.id, start.current.left, start.current.top);
        return;
      }

      if (isOffScreen || fastSwipe) {
        haptics.success();
        setTimeout(() => onClose(tile.id), 180);
      } else {
        // Snap back to fully in-bounds
        const nx = Math.max(0, Math.min(lastX, window.innerWidth - tile.width));
        const ny = Math.max(0, Math.min(lastY, window.innerHeight - tile.height - 100));
        if (nx !== lastX || ny !== lastY) onDragPosition(tile.id, nx, ny);
        haptics.click();
      }
    };

    window.addEventListener("mousemove", onMove as EventListener);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove as EventListener, { passive: false });
    window.addEventListener("touchend", onUp);
    window.addEventListener("touchcancel", onUp);
  };

  const handleResize = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
    setIsResizing(true);
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    resizeStart.current = {
      x: clientX,
      y: clientY,
      width: tile.width,
      height: tile.height,
      left: tile.x,
      top: tile.y,
    };

    const onMove = (ev: MouseEvent | TouchEvent) => {
      if ('touches' in ev) ev.preventDefault();
      const cx = 'touches' in ev ? ev.touches[0].clientX : ev.clientX;
      const cy = 'touches' in ev ? ev.touches[0].clientY : ev.clientY;
      const dx = cx - resizeStart.current.x;
      const dy = cy - resizeStart.current.y;
      const newWidth = Math.max(300, Math.min(resizeStart.current.width + dx, window.innerWidth - resizeStart.current.left));
      const newHeight = Math.max(200, Math.min(resizeStart.current.height + dy, window.innerHeight - resizeStart.current.top - 100));
      onResize(tile.id, newWidth, newHeight);
    };

    const onUp = () => {
      setIsResizing(false);
      haptics.click();
      window.removeEventListener("mousemove", onMove as EventListener);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove as EventListener);
      window.removeEventListener("touchend", onUp);
      window.removeEventListener("touchcancel", onUp);
    };

    window.addEventListener("mousemove", onMove as EventListener);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove as EventListener, { passive: false });
    window.addEventListener("touchend", onUp);
    window.addEventListener("touchcancel", onUp);
  };

  if (tile.minimized) return null;

  const isFullscreen = tile.maximized || false;

  // Every tile currently shares one active profile, so this is the same
  // color everywhere for now — but reading it per-tile here means the glow
  // ring already follows individual tile branding the day tiles can carry
  // their own color, same forward-compat approach as the top bar's glow.
  const tileGlowColor = tile.profile?.brand?.primaryColor ?? accent;

  // Carousel items sit in a flex row (see .spatial-tile-carousel-item in
  // karma-ac.css) and just fill it — the usual absolute x/y/width/height
  // positioning (and its matching framer-motion animate props) would fight
  // the flex layout instead of cooperating with it.
  return (
    <motion.div
      className={`spatial-tile ${chromeless ? "spatial-tile-chromeless rounded-2xl" : "glass-card dark:glass-card-dark"} ${isFullscreen ? 'tile-fullscreen' : ''} ${carousel ? 'spatial-tile-carousel-tile spatial-tile-carousel-item' : ''} spatial-tile-glow${tile.hasUnseen ? " spatial-tile-urgent" : ""}`}
      style={carousel ? {
        // Sizing comes entirely from .spatial-tile-carousel-item's
        // flex-basis in CSS — setting width/height here too would compete
        // with that instead of just deferring to it. position:relative
        // (not the flex item's default static) is still needed so the
        // absolutely-positioned children below (glow ring, drag handle,
        // ambient overlay) anchor to this tile and not some further-up
        // ancestor.
        position: "relative",
        "--accent": accent,
        "--tile-glow-color": tileGlowColor,
        overflow: "hidden",
      } as React.CSSProperties : {
        position: isFullscreen ? "fixed" : "absolute",
        left: isFullscreen ? 0 : tile.x,
        top: isFullscreen ? 0 : tile.y,
        width: isFullscreen ? "100vw" : tile.width,
        height: isFullscreen ? "100dvh" : tile.height,
        zIndex: isFullscreen ? 11000 : tile.zIndex || 1000,
        "--accent": accent,
        "--tile-glow-color": tileGlowColor,
        overflow: "hidden",
      } as React.CSSProperties}
      initial={{ opacity: 0, scale: 0.95, y: carousel ? 0 : 20 }}
      animate={carousel ? { opacity: 1, scale: 1, y: 0 } : {
        opacity: 1,
        scale: 1,
        y: 0,
        left: isFullscreen ? 0 : tile.x,
        top: isFullscreen ? 0 : tile.y,
        width: isFullscreen ? "100vw" : tile.width,
        height: isFullscreen ? "100dvh" : tile.height,
      }}
      exit={{ opacity: 0, scale: 0.8, y: 30, transition: { duration: 0.2 } }}
      transition={{
        type: "spring",
        stiffness: 400,
        damping: 30,
      }}
      onMouseDown={(e) => {
        // Bring tile to front when clicked anywhere, but don't start a drag from the body
        if (
          !(e.target as HTMLElement).closest('.tile-controls') &&
          !(e.target as HTMLElement).closest('.resize-handle') &&
          !(e.target as HTMLElement).closest('.tile-drag-handle')
        ) {
          onMoveFront(tile.id);
        }
      }}
      onTouchStart={(e) => {
        if (
          !(e.target as HTMLElement).closest('.tile-controls') &&
          !(e.target as HTMLElement).closest('.resize-handle') &&
          !(e.target as HTMLElement).closest('.tile-drag-handle')
        ) {
          onMoveFront(tile.id);
        }
      }}
      whileHover={{ scale: isDragging ? 1.02 : isFullscreen ? 1 : 1.005 }}
    >
      {/* Ambient glow effect */}
      {!chromeless && (
        <motion.div
          className="absolute inset-0 rounded-2xl pointer-events-none"
          style={{
            background: `radial-gradient(circle at center, ${accent}15, transparent 70%)`,
          }}
          animate={{
            opacity: [0.3, 0.5, 0.3],
            scale: [1, 1.05, 1],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      )}

      {chromeless ? (
        <>
          {/* Thin drag-handle strip: icon + name, nothing else. Window
              controls (minimize/maximize/close) used to float here too, but
              now live exclusively in the consciousness bar for whichever
              tile is active (see KarmaDynamicIsland.tsx) — clicking this
              tile brings it to front and shifts the bar to it. Hidden when
              fullscreen: nothing to drag at 100vw, and the bar already shows
              the name. */}
          {!isFullscreen && (
            <div
              className="tile-drag-handle absolute left-2 z-20 flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-black/40 backdrop-blur-md border border-white/10 cursor-grab active:cursor-grabbing touch-manipulation max-w-[70%]"
              style={{ top: 8 }}
              onMouseDown={onDragStart}
              onTouchStart={onDragStart}
              title="Drag tile"
              aria-label="Drag tile"
            >
              <span className="text-xs shrink-0">{tile.icon || "🧠"}</span>
              <span className="text-[11px] font-medium text-white/80 truncate">{tile.name}</span>
              <GripHorizontal className="w-3 h-3 text-white/40 shrink-0" />
            </div>
          )}

          {/* Full-bleed content — reserves space for the consciousness bar
              up top when fullscreen, since this tile no longer draws its
              own header there. */}
          <div
            className="tile-content h-full w-full overflow-hidden"
            style={isFullscreen ? { paddingTop: "max(64px, calc(env(safe-area-inset-top) + 54px))" } : undefined}
          >

            {tile.content || (
              <div className="h-full flex flex-col items-center justify-center text-white/60 text-xs p-4">
                <p className="font-semibold mb-1">{tile.name}</p>
                <p className="opacity-60">Drag handle • Resize corner • Controls top-right</p>
              </div>
            )}
          </div>

          {/* Resize handle — meaningless in carousel mode, tiles fill their
              flex slot at 100%/100% rather than a freely resizable box. */}
          {!isFullscreen && !carousel && (
            <motion.div
              className="resize-handle absolute bottom-2 right-2 w-7 h-7 cursor-nwse-resize rounded-full flex items-center justify-center"
              style={{
                background: `radial-gradient(circle at 70% 70%, ${accent}70, transparent 70%)`,
              }}
              onMouseDown={handleResize}
              onTouchStart={handleResize}
              whileHover={{ scale: 1.15 }}
              whileTap={{ scale: 0.95 }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 22L11 11" />
                <path d="M16 22h6v-6" />
                <path d="M22 16V2H8" />
              </svg>
            </motion.div>
          )}
        </>
      ) : (
        <>
          {/* Thin name strip, doubling as the drag handle — window controls
              live exclusively in the consciousness bar for whichever tile
              is active now (see KarmaDynamicIsland.tsx). Hidden when
              fullscreen, same reasoning as the chromeless variant above. */}
          {!isFullscreen && (
            <div
              className="tile-header flex items-center gap-2 px-3 py-1.5 border-b border-white/10 bg-white/5 backdrop-blur-sm rounded-t-2xl cursor-grab active:cursor-grabbing"
              onMouseDown={onDragStart}
              onTouchStart={onDragStart}
            >
              <span className="text-sm">{tile.icon || "🧠"}</span>
              <span className="font-medium text-white text-xs truncate">{tile.name}</span>
              <GripHorizontal className="w-3 h-3 text-white/40 ml-auto shrink-0" />
            </div>
          )}

          {/* Window content */}
          <div
            className="tile-content p-4 h-full overflow-auto"
            style={isFullscreen ? { paddingTop: "max(64px, calc(env(safe-area-inset-top) + 54px))" } : undefined}
          >
            {tile.content || (
              <div className="text-white/80">
                <p className="font-semibold mb-2">{tile.name}</p>
                <p className="text-xs opacity-70">
                  Drag to move • Resize from corner • Click controls to minimize/maximize
                </p>
              </div>
            )}
          </div>

          {/* Resize handle */}
          {!isFullscreen && (
            <motion.div
              className="resize-handle absolute bottom-0 right-0 w-4 h-4 cursor-nwse-resize"
              style={{
                background: `linear-gradient(135deg, ${accent}40, transparent)`,
              }}
              onMouseDown={handleResize}
              onTouchStart={handleResize}
              whileHover={{ scale: 1.2 }}
              whileTap={{ scale: 0.9 }}
            />
          )}
        </>
      )}
    </motion.div>
  );
}

